import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import { GridPager } from "../../common/pagination/GridPager";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableToolbar from "../../common/ui/table-toolbar/TableToolbar";
import FilterTabs from "../../common/ui/filter-tabs/FilterTabs";
import TableContainer from "../../common/ui/table-container/TableContainer";
import DataTable from "../../common/ui/data-table/DataTable";
import RowsPerPageSelect from "../../common/ui/rows-per-page-select/RowsPerPageSelect";
import "rc-pagination/assets/index.css";
import "./Documents.css";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import { uploadDocumentToS3 } from "../../../utils/common";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";

const categoryLabels = {
  InternalPolicy: "Internal Policy",
  Regulatory: "Regulatory",
  Training: "Training",
  TermsAndConditions: "Terms & Conditions",
  EmployeeHandbook: "Employee Handbook",
  Safety: "Safety",
  Other: "Other",
};

const getCategoryLabel = (category) => categoryLabels[category] || category;

// Matches backend CompanyDocumentCategory : short (order must match C# enum)
const COMPANY_DOCUMENT_CATEGORY_VALUE = {
  InternalPolicy: 0,
  Regulatory: 1,
  Training: 2,
  TermsAndConditions: 3,
  EmployeeHandbook: 4,
  Safety: 5,
  Other: 6,
};

const CATEGORY_VALUE_TO_KEY = Object.fromEntries(
  Object.entries(COMPANY_DOCUMENT_CATEGORY_VALUE).map(([k, v]) => [v, k])
);

/** Backend CompanyDocumentPriority : short */
const COMPANY_DOCUMENT_PRIORITY_VALUE = {
  Low: 0,
  Medium: 1,
  High: 2,
  Critical: 3,
  FollowUp: 4,
};

const priorityLabels = {
  0: "Low",
  1: "Medium",
  2: "High",
  3: "Critical",
  4: "Follow-up",
};

/** Backend CompanyDocumentOfficeScope */
const CompanyDocumentOfficeScope = {
  AllOffices: 0,
  SpecificOffices: 1,
};

/** Coerce API total fields (sometimes sent as string). */
const coerceTotalNumber = (v) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && String(v).trim() !== "") {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
};

/** Total rows across all pages — camelCase + PascalCase; supports string totals. */
const extractTotalCount = (obj) => {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return undefined;
  const keys = [
    "total",
  ];
  for (const k of keys) {
    const n = coerceTotalNumber(obj[k]);
    if (n !== undefined) return n;
  }
  if (obj.pagination && typeof obj.pagination === "object") {
    const nested = extractTotalCount(obj.pagination);
    if (nested !== undefined) return nested;
  }
  return undefined;
};

/**
 * @param data - Inner payload from `response.data.data` (or equivalent).
 * @param responseRoot - Full `response.data` so we can read `total` / `status` siblings when the list is nested.
 */
const normalizeCompanyDocumentsPayload = (data, responseRoot = null) => {
  const pickTotal = (obj) => {
    const fromPayload = extractTotalCount(obj);
    if (fromPayload !== undefined) return fromPayload;
    if (responseRoot != null && typeof responseRoot === "object") {
      const fromRoot = extractTotalCount(responseRoot);
      if (fromRoot !== undefined) return fromRoot;
    }
    return undefined;
  };

  if (data == null) return { items: [], totalCount: undefined };
  if (Array.isArray(data)) {
    const fromRoot =
      responseRoot != null && typeof responseRoot === "object"
        ? extractTotalCount(responseRoot)
        : undefined;
    return { items: data, totalCount: fromRoot };
  }
  if (Array.isArray(data.items)) {
    return { items: data.items, totalCount: pickTotal(data) };
  }
  if (Array.isArray(data.data)) {
    return { items: data.data, totalCount: pickTotal(data) };
  }
  if (Array.isArray(data.documents)) {
    return { items: data.documents, totalCount: pickTotal(data) };
  }
  return { items: [], totalCount: pickTotal(data) };
};

/** Offices this document applies to (from GetCompanyDocuments / admin view). */
const parseApplicableOffices = (doc) => {
  const raw = doc?.applicableOffices ?? doc?.ApplicableOffices ?? null;
  if (raw == null) return [];
  let arr;
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === "object") {
    if ("id" in raw || "officeId" in raw || "OfficeId" in raw) {
      arr = [raw];
    } else {
      const values = Object.values(raw);
      arr = values.every((v) => v && typeof v === "object") ? values : [raw];
    }
  } else {
    return [];
  }
  return arr
    .map((x) => {
      if (!x || typeof x !== "object") return null;
      const idRaw = x.id ?? x.officeId ?? x.OfficeId;
      const nameRaw = x.name ?? x.officeName ?? x.Name ?? "";
      const name = String(nameRaw).trim();
      if (idRaw === undefined || idRaw === null) return null;
      const id =
        typeof idRaw === "number"
          ? idRaw
          : typeof idRaw === "string" && /^\d+$/.test(idRaw.trim())
            ? Number(idRaw.trim())
            : idRaw;
      return { id, name: name || `Office ${idRaw}` };
    })
    .filter(Boolean);
};

/** Short label for grid: count only (details in View). */
const formatDistributionGridLabel = (row) => {
  if (row.officeScope === CompanyDocumentOfficeScope.AllOffices) return "All offices";
  const n =
    Array.isArray(row.applicableOffices) && row.applicableOffices.length > 0
      ? row.applicableOffices.length
      : row.officeIds?.length ?? 0;
  if (n <= 0) return "—";
  return n === 1 ? "1 office" : `${n} offices`;
};

/** Hover tooltip: office names when known, else count. */
const formatDistributionTooltip = (row) => {
  if (row.officeScope === CompanyDocumentOfficeScope.AllOffices) return "All offices";
  const offices = row.applicableOffices;
  if (Array.isArray(offices) && offices.length > 0) {
    return offices.map((o) => o.name).join(", ");
  }
  const n = row.officeIds?.length ?? 0;
  if (n > 0) return `${n} office${n === 1 ? "" : "s"}`;
  return "";
};

/** Same body shape as edit save — for grid switch toggles. */
const buildGridUpdatePayload = (row, patch) => {
  const status = patch.status !== undefined ? patch.status : row.status;
  let ackRequired = patch.ackRequired !== undefined ? patch.ackRequired : row.ackRequired;
  let allowRejection = patch.allowRejection !== undefined ? patch.allowRejection : row.allowRejection;
  if (!ackRequired) allowRejection = true;
  const categoryKey =
    row.type && COMPANY_DOCUMENT_CATEGORY_VALUE[row.type] !== undefined ? row.type : "Other";
  const categoryValue =
    COMPANY_DOCUMENT_CATEGORY_VALUE[categoryKey] ?? COMPANY_DOCUMENT_CATEGORY_VALUE.Other;
  const title = (row.name || "").trim() || "Untitled";
  return {
    documentId: String(row.id),
    title,
    category: categoryValue,
    priority: Number(row.priority),
    isActive: !!status,
    requiresAcceptance: !!ackRequired,
    allowRejection: !!allowRejection,
    officeScope: row.officeScope,
    officeIds: Array.isArray(row.officeIds) ? row.officeIds.map(Number) : [],
  };
};

const mapApiDocumentToRow = (doc) => {
  const rawCat = doc.category;
  let typeKey = "Other";
  if (typeof rawCat === "string" && categoryLabels[rawCat]) {
    typeKey = rawCat;
  } else if (rawCat !== undefined && rawCat !== null) {
    const n = Number(rawCat);
    typeKey = CATEGORY_VALUE_TO_KEY[n] ?? "Other";
  }
  const rawCompletion =
    doc.completionRate ??
    doc.acknowledgementCompletionRate ??
    doc.ackCompletionRate ??
    doc.completionPercentage ??
    doc.acknowledgementCompletionPercentage;
  let completionRate = null;
  if (rawCompletion !== undefined && rawCompletion !== null && rawCompletion !== "") {
    const n = Number(rawCompletion);
    if (!Number.isNaN(n)) {
      completionRate = n <= 1 && n >= 0 ? Math.round(n * 100) : Math.round(Math.min(100, Math.max(0, n)));
    }
  }

  const applicableOffices = parseApplicableOffices(doc);
  let officeIds = Array.isArray(doc.officeIds)
    ? doc.officeIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n))
    : [];
  if (officeIds.length === 0 && applicableOffices.length > 0) {
    officeIds = applicableOffices
      .map((o) => o.id)
      .map((id) => (typeof id === "number" ? id : Number(id)))
      .filter((n) => !Number.isNaN(n));
  }

  return {
    id: doc.id,
    name: doc.title ?? doc.name ?? "",
    description: doc.description ?? "",
    type: typeKey,
    status:
      doc.isActive === undefined || doc.isActive === null ? true : !!doc.isActive,
    ackRequired: !!doc.requiresAcceptance,
    allowRejection: !!doc.allowRejection,
    priority: doc.priority !== undefined && doc.priority !== null ? Number(doc.priority) : 1,
    officeScope:
      doc.officeScope !== undefined && doc.officeScope !== null
        ? Number(doc.officeScope)
        : CompanyDocumentOfficeScope.AllOffices,
    officeIds,
    applicableOffices,
    completionRate,
    acknowledgementCompletedCount:
      doc.acknowledgementCompletedCount ??
      doc.completedAcknowledgementCount ??
      doc.acknowledgedCount ??
      null,
    acknowledgementTotalCount:
      doc.acknowledgementTotalCount ?? doc.totalAcknowledgementCount ?? doc.assigneeCount ?? null,
  };
};

/** From GetDocumentForAdminView — CompanyDocumentAcknowledgementSummary (camelCase + PascalCase). */
const parseAcknowledgementSummary = (payload) => {
  const raw =
    payload?.acknowledgementSummary ?? payload?.AcknowledgementSummary ?? null;
  if (raw == null || typeof raw !== "object") return null;
  const n = (v) => {
    const x = Number(v);
    return Number.isNaN(x) ? 0 : x;
  };
  return {
    totalAssigned: n(raw.totalAssigned ?? raw.TotalAssigned),
    acceptedCount: n(raw.acceptedCount ?? raw.AcceptedCount),
    rejectedCount: n(raw.rejectedCount ?? raw.RejectedCount),
    pendingCount: n(raw.pendingCount ?? raw.PendingCount),
    acknowledgementRatePercent: Math.min(
      100,
      Math.max(0, n(raw.acknowledgementRatePercent ?? raw.AcknowledgementRatePercent))
    ),
    completionRatePercent: Math.min(
      100,
      Math.max(0, n(raw.completionRatePercent ?? raw.CompletionRatePercent))
    ),
  };
};

const normalizeAdminViewPayload = (payload) => {
  if (!payload) return null;
  const document = payload.document ?? payload.Document;
  const fileContentBase64 =
    payload.fileContentBase64 ?? payload.FileContentBase64 ?? "";
  const contentType =
    payload.contentType ?? payload.ContentType ?? "application/octet-stream";
  const acknowledgementSummary = parseAcknowledgementSummary(payload);
  return { document, fileContentBase64, contentType, acknowledgementSummary };
};

const DocumentAdminAcknowledgementSummary = ({ summary, allowRejection }) => {
  const s = summary;
  const responded = s.acceptedCount + s.rejectedCount;

  return (
    <div className="documents-detail-ack-summary">
      <div className="documents-detail-completion-head">
        <span className="documents-detail-completion-label">Response completion</span>
        <span className="documents-detail-completion-meta">
          {responded} of {s.totalAssigned} assigned employees responded
        </span>
      </div>
      <div
        className="documents-ack-progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={s.completionRatePercent}
        aria-label={`${s.completionRatePercent}% of assigned employees have responded`}
      >
        <div className="documents-ack-progress-fill" style={{ width: `${s.completionRatePercent}%` }} />
      </div>
      <div className="documents-ack-progress-caption">
        <span className="documents-ack-progress-percent">{s.completionRatePercent}%</span>
        <span className="documents-ack-progress-sub">overall response (accepted or rejected)</span>
      </div>
      <div className="documents-ack-stat-row">
        <div className="documents-ack-stat documents-ack-stat--accepted">
          <span className="documents-ack-stat-value">{s.acceptedCount}</span>
          <span className="documents-ack-stat-label">Accepted</span>
        </div>
        {allowRejection ? (
          <div className="documents-ack-stat documents-ack-stat--rejected">
            <span className="documents-ack-stat-value">{s.rejectedCount}</span>
            <span className="documents-ack-stat-label">Rejected</span>
          </div>
        ) : null}
        <div className="documents-ack-stat documents-ack-stat--pending">
          <span className="documents-ack-stat-value">{s.pendingCount}</span>
          <span className="documents-ack-stat-label">Pending</span>
        </div>
      </div>
      <div className="documents-ack-acceptance-row">
        <span className="documents-ack-acceptance-label">Acceptance rate</span>
        <span className="documents-ack-acceptance-value">{s.acknowledgementRatePercent}%</span>
        <span className="documents-ack-acceptance-hint">
          of assigned employees who acknowledged (accepted only)
        </span>
      </div>
    </div>
  );
};

const buildBlobUrlFromBase64 = (base64, contentType) => {
  if (!base64 || typeof base64 !== "string") return null;
  try {
    const clean = base64.replace(/\s/g, "");
    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: contentType || "application/octet-stream" });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const isBrowserPreviewableMime = (mime) => {
  if (!mime || typeof mime !== "string") return false;
  const m = mime.toLowerCase();
  return (
    m.includes("pdf") ||
    m.startsWith("image/") ||
    m.startsWith("text/plain") ||
    m.includes("text/html")
  );
};

const publicationBadgeClass = (published) =>
  published
    ? "documents-detail-badge documents-detail-badge--published"
    : "documents-detail-badge documents-detail-badge--draft";

const priorityBadgeClass = (priority) => {
  const n = Number(priority);
  const map = {
    0: "documents-detail-badge documents-detail-badge--priority-low",
    1: "documents-detail-badge documents-detail-badge--priority-medium",
    2: "documents-detail-badge documents-detail-badge--priority-high",
    3: "documents-detail-badge documents-detail-badge--priority-critical",
    4: "documents-detail-badge documents-detail-badge--priority-followup",
  };
  return map[n] ?? "documents-detail-badge documents-detail-badge--neutral";
};

const DocumentLibrary = () => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const adminPreviewUrlRef = useRef(null);
  /** When filters/page size change, total must not carry over from previous list query. */
  const listFilterKeyRef = useRef("");
  const [adminView, setAdminView] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [filterOfficeId, setFilterOfficeId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const [companyOffices, setCompanyOffices] = useState([]);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  /** Client-side category filter for current page rows; use "all" or a category key (e.g. InternalPolicy). */
  const [categoryFilter, setCategoryFilter] = useState("all");
  /** Document id while a grid switch update is in flight (Publication / Ack / Allow rejection). */
  const [gridUpdatingId, setGridUpdatingId] = useState(null);

  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("documents");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({
    Title: "",
    Description: "",
    Category: "Other",
    Priority: 1,
    AllOffices: true,
    OfficeIds: [],
    RequiresAcceptance: false,
    AllowRejection: true,
    IsActive: true,
  });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editDocumentId, setEditDocumentId] = useState(null);
  /** When set, shows delete confirmation modal (same overlay pattern as upload/edit). */
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    Title: "",
    Category: "Other",
    Priority: 1,
    AllOffices: true,
    OfficeIds: [],
    RequiresAcceptance: false,
    AllowRejection: true,
    IsActive: true,
  });

  const sortedDocuments = useMemo(() => {
    const list = [...documents];
    const mult = sortDir === "asc" ? 1 : -1;
    const key = sortKey;
    const officeRank = (row) =>
      row.officeScope === CompanyDocumentOfficeScope.AllOffices
        ? 0
        : 1000 + (row.officeIds?.length ?? 0);
    list.sort((a, b) => {
      let va;
      let vb;
      if (key === "officeScope") {
        va = officeRank(a);
        vb = officeRank(b);
      } else {
        va = a[key];
        vb = b[key];
      }
      if (key === "name" || key === "type") {
        va = String(va ?? "").toLowerCase();
        vb = String(vb ?? "").toLowerCase();
      } else if (typeof va === "boolean") {
        va = va ? 1 : 0;
        vb = vb ? 1 : 0;
      } else if (key !== "officeScope") {
        va = va ?? 0;
        vb = vb ?? 0;
        if (typeof va === "string") va = va.toLowerCase();
        if (typeof vb === "string") vb = vb.toLowerCase();
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
    return list;
  }, [documents, sortKey, sortDir]);

  const distinctCategoriesOnPage = useMemo(() => {
    const keys = [...new Set(documents.map((d) => d.type).filter(Boolean))];
    keys.sort((a, b) =>
      getCategoryLabel(a).localeCompare(getCategoryLabel(b), undefined, { sensitivity: "base" })
    );
    return keys;
  }, [documents]);

  const categoryTabs = useMemo(
    () => [
      { key: "all", label: "All files" },
      ...distinctCategoriesOnPage.map((cat) => ({ key: cat, label: getCategoryLabel(cat) })),
    ],
    [distinctCategoriesOnPage]
  );

  const tableColumns = useMemo(
    () => [
      { key: "name", label: "Document", sortable: true },
      { key: "type", label: "Category", sortable: true },
      { key: "priority", label: "Priority", sortable: true },
      { key: "officeScope", label: "Offices", sortable: true },
      { key: "status", label: "Publication", sortable: true },
      { key: "ackRequired", label: "Acknowledgement", sortable: true },
      { key: "allowRejection", label: "Allow Rejection", sortable: true },
      { key: "actions", label: "Actions", sortable: false, headerClassName: "documents-table-th-actions" },
    ],
    []
  );

  useEffect(() => {
    if (
      categoryFilter !== "all" &&
      distinctCategoriesOnPage.length > 0 &&
      !distinctCategoriesOnPage.includes(categoryFilter)
    ) {
      setCategoryFilter("all");
    }
  }, [categoryFilter, distinctCategoriesOnPage]);

  const filteredSortedDocuments = useMemo(() => {
    if (categoryFilter === "all") return sortedDocuments;
    return sortedDocuments.filter((d) => d.type === categoryFilter);
  }, [sortedDocuments, categoryFilter]);

  const tableEmptyMessage = useMemo(() => {
    if (sortedDocuments.length === 0) return "No documents found.";
    if (filteredSortedDocuments.length === 0) {
      return 'No documents in this category on the current page. Try "All files" or another page.';
    }
    return "No documents found.";
  }, [sortedDocuments.length, filteredSortedDocuments.length]);

  /** Company offices plus any offices only seen on ApplicableOffices (stable filter labels). */
  const officesForFilter = useMemo(() => {
    const byKey = new Map();
    const add = (id, label) => {
      if (id === undefined || id === null || id === "") return;
      const key = String(id);
      if (!byKey.has(key)) {
        byKey.set(key, { id, label: label || `Office ${id}` });
      }
    };
    companyOffices.forEach((o) => add(o.id, o.label));
    documents.forEach((d) => {
      (d.applicableOffices || []).forEach((ao) => add(ao.id, ao.name));
    });
    return Array.from(byKey.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" })
    );
  }, [companyOffices, documents]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 380);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const fetchCompanyDocuments = async (overrides = {}) => {
    const usePage = overrides.page ?? page;
    const usePageSize = overrides.pageSize ?? pageSize;
    const useIncludeInactive =
      overrides.includeInactive !== undefined ? overrides.includeInactive : includeInactive;
    const useOfficeId =
      overrides.filterOfficeId !== undefined ? overrides.filterOfficeId : filterOfficeId;

    setListLoading(true);
    try {
      const { CompanyDocuments } = config.REST_API;
      const searchTrim = debouncedSearch.trim();
      const hasSearch = searchTrim.length > 0;
      const params = {
        includeInactive: useIncludeInactive,
        page: usePage,
        pageSize: usePageSize,
      };
      if (useOfficeId != null && useOfficeId !== "") {
        const num = Number(useOfficeId);
        params.officeId = Number.isNaN(num) ? useOfficeId : num;
      }
      if (hasSearch) {
        params.search = searchTrim;
      }
      const endpoint = hasSearch
        ? CompanyDocuments.SearchCompanyDocuments
        : CompanyDocuments.GetCompanyDocuments;
      const res = await axiosBasic.get(endpoint, { params });
      const responseBody = res.data || {};
      const { success, data, message } = responseBody;
      if (!success) {
        Common.showErrorMessage(message || "Could not load documents.");
        setDocuments([]);
        setTotalCount(0);
        return;
      }
      const { items, totalCount: tc } = normalizeCompanyDocumentsPayload(data, responseBody);
      const rows = items.map(mapApiDocumentToRow);
      setDocuments(rows);

      const listFilterKey = `${useIncludeInactive}|${useOfficeId ?? ""}|${usePageSize}|${searchTrim}`;
      const queryChanged = listFilterKeyRef.current !== listFilterKey;
      listFilterKeyRef.current = listFilterKey;
      const inferred = (usePage - 1) * usePageSize + rows.length;

      setTotalCount((prev) => {
        if (typeof tc === "number" && !Number.isNaN(tc)) return tc;
        if (queryChanged) return inferred;
        if (rows.length < usePageSize) return inferred;
        return Math.max(prev, inferred);
      });
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Could not load documents.");
      setDocuments([]);
      setTotalCount(0);
    } finally {
      setListLoading(false);
    }
  };

  /** Same rc-pagination / GridPager behavior as Users grid */
  const handleDocumentsPagerChange = (current, nextPageSize) => {
    const resolvedSize =
      nextPageSize != null && nextPageSize !== ""
        ? Number(nextPageSize)
        : pageSize;
    if (resolvedSize !== pageSize) {
      setPageSize(resolvedSize);
      setPage(1);
    } else {
      setPage(current);
    }
  };

  useEffect(() => {
    const searchChanged = prevDebouncedSearchRef.current !== debouncedSearch;
    prevDebouncedSearchRef.current = debouncedSearch;
    if (searchChanged) {
      setPage(1);
    }
    fetchCompanyDocuments({ page: searchChanged ? 1 : undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, includeInactive, filterOfficeId, debouncedSearch]);

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const { Office } = config.REST_API;
        const res = await axiosBasic.get(Office.GetCompanyOffices);
        const { success, data } = res.data || {};
        if (success && Array.isArray(data)) {
          setCompanyOffices(
            data.map((o) => ({
              id: o.id,
              label: o.countryName ? `${o.name} (${o.countryName})` : o.name,
            }))
          );
        }
      } catch {
        setCompanyOffices([]);
      }
    };
    loadOffices();
  }, []);

  useEffect(() => {
    // AllowRejection only makes sense when RequiresAcceptance is enabled.
    if (!uploadForm.RequiresAcceptance) {
      setUploadForm((prev) => ({ ...prev, AllowRejection: true }));
    }
  }, [uploadForm.RequiresAcceptance]);

  useEffect(() => {
    if (!editForm.RequiresAcceptance) {
      setEditForm((prev) => ({ ...prev, AllowRejection: true }));
    }
  }, [editForm.RequiresAcceptance]);

  const handleSortColumn = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const updateDocumentFromGrid = async (row, field) => {
    if (gridUpdatingId != null) return;
    if (field === "allowRejection" && !row.ackRequired) return;

    const patch =
      field === "status"
        ? { status: !row.status }
        : field === "ackRequired"
          ? { ackRequired: !row.ackRequired }
          : { allowRejection: !row.allowRejection };

    const payload = buildGridUpdatePayload(row, patch);
    setGridUpdatingId(row.id);
    try {
      const { CompanyDocuments } = config.REST_API;
      const path = `${CompanyDocuments.UpdateCompanyDocument}/${encodeURIComponent(payload.documentId)}`;
      const res = await axiosBasic.put(path, payload, {
        headers: { "Content-Type": "application/json" },
      });
      const result = res?.data;
      if (!result || result.success !== true) {
        Common.showErrorMessage(result?.message || "Could not update document.");
        return;
      }
      setDocuments((prev) =>
        prev.map((item) => {
          if (item.id !== row.id) return item;
          const next = { ...item };
          if (field === "status") next.status = !row.status;
          if (field === "ackRequired") {
            next.ackRequired = !row.ackRequired;
            if (!next.ackRequired) next.allowRejection = true;
          }
          if (field === "allowRejection") next.allowRejection = !row.allowRejection;
          return next;
        })
      );
      setAdminView((prev) => {
        if (!prev || String(prev.documentId) !== String(row.id) || !prev.row) return prev;
        const nr = { ...prev.row };
        if (field === "status") nr.status = !row.status;
        if (field === "ackRequired") {
          nr.ackRequired = !row.ackRequired;
          if (!nr.ackRequired) nr.allowRejection = true;
        }
        if (field === "allowRejection") nr.allowRejection = !row.allowRejection;
        return { ...prev, row: nr };
      });
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Update failed.");
    } finally {
      setGridUpdatingId(null);
    }
  };

  const revokeAdminPreviewUrl = () => {
    if (adminPreviewUrlRef.current) {
      URL.revokeObjectURL(adminPreviewUrlRef.current);
      adminPreviewUrlRef.current = null;
    }
  };

  const closeAdminView = () => {
    revokeAdminPreviewUrl();
    setAdminView(null);
  };

  const openAdminView = async (documentId, listRow = null) => {
    revokeAdminPreviewUrl();
    setAdminView({ loading: true, documentId });
    try {
      const { CompanyDocuments } = config.REST_API;
      const id = encodeURIComponent(String(documentId));
      const res = await axiosBasic.get(`${CompanyDocuments.GetDocumentForAdminView}/${id}`);
      const { success, data, message } = res.data || {};
      if (!success) {
        setAdminView({
          loading: false,
          error: message || "Could not load document.",
          documentId,
        });
        return;
      }
      const raw = data?.data ?? data;
      const normalized = normalizeAdminViewPayload(raw);
      if (!normalized?.document) {
        setAdminView({
          loading: false,
          error: "Invalid response from server.",
          documentId,
        });
        return;
      }
      let row = mapApiDocumentToRow(normalized.document);
      if (
        listRow?.applicableOffices?.length > 0 &&
        (!row.applicableOffices || row.applicableOffices.length === 0)
      ) {
        row = { ...row, applicableOffices: listRow.applicableOffices };
      }
      const previewUrl = buildBlobUrlFromBase64(
        normalized.fileContentBase64,
        normalized.contentType
      );
      if (previewUrl) adminPreviewUrlRef.current = previewUrl;
      const fileName =
        normalized.document.fileName ??
        normalized.document.FileName ??
        row.name ??
        "document";
      setAdminView({
        loading: false,
        row,
        previewUrl,
        contentType: normalized.contentType,
        documentId,
        fileName,
        acknowledgementSummary: normalized.acknowledgementSummary ?? null,
      });
    } catch (err) {
      setAdminView({
        loading: false,
        error: err?.response?.data?.message || err?.message || "Could not load document.",
        documentId,
      });
    }
  };

  useEffect(() => () => revokeAdminPreviewUrl(), []);

  const removeDocumentFromLocalState = (id) => {
    setAdminView((prev) => {
      if (prev && String(prev.documentId) === String(id)) {
        revokeAdminPreviewUrl();
        return null;
      }
      return prev;
    });
    setDocuments((prev) => prev.filter((item) => item.id !== id));
  };

  const openDeleteConfirm = (row) => {
    setDeleteConfirm({ id: row.id, name: row.name || "this document" });
  };

  const handleConfirmDeleteDocument = async () => {
    if (!deleteConfirm?.id) return;
    const documentId = deleteConfirm.id;
    setDeleteLoading(true);
    try {
      const { CompanyDocuments } = config.REST_API;
      const path = `${CompanyDocuments.DeleteCompanyDocument}/${encodeURIComponent(String(documentId))}`;
      const res = await axiosBasic.delete(path);
      const result = res?.data;
      if (!result || result.success !== true) {
        Common.showErrorMessage(result?.message || "Could not delete document.");
        return;
      }
      removeDocumentFromLocalState(documentId);
      setDeleteConfirm(null);
      Common.showSuccessMessage(result.message || "Document deleted.");
      await fetchCompanyDocuments();
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetUploadDialog = () => {
    setSelectedFile(null);
    setUploadForm({
      Title: "",
      Description: "",
      Category: "Other",
      Priority: 1,
      AllOffices: true,
      OfficeIds: [],
      RequiresAcceptance: false,
      AllowRejection: true,
      IsActive: true,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const toggleUploadOfficeId = (officeId) => {
    setUploadForm((prev) => {
      const id = Number(officeId);
      const has = prev.OfficeIds.includes(id);
      const OfficeIds = has ? prev.OfficeIds.filter((x) => x !== id) : [...prev.OfficeIds, id];
      return { ...prev, OfficeIds };
    });
  };

  const resetEditDialog = () => {
    setEditDocumentId(null);
    setEditForm({
      Title: "",
      Category: "Other",
      Priority: 1,
      AllOffices: true,
      OfficeIds: [],
      RequiresAcceptance: false,
      AllowRejection: true,
      IsActive: true,
    });
  };

  const openEditDocument = (row) => {
    setEditDocumentId(row.id);
    setEditForm({
      Title: row.name || "",
      Category: row.type && COMPANY_DOCUMENT_CATEGORY_VALUE[row.type] !== undefined ? row.type : "Other",
      Priority:
        row.priority !== undefined && row.priority !== null
          ? Number(row.priority)
          : COMPANY_DOCUMENT_PRIORITY_VALUE.Medium,
      AllOffices: row.officeScope === CompanyDocumentOfficeScope.AllOffices,
      OfficeIds: Array.isArray(row.officeIds) ? row.officeIds.map(Number) : [],
      RequiresAcceptance: !!row.ackRequired,
      AllowRejection: !!row.allowRejection,
      IsActive: !!row.status,
    });
    setIsEditOpen(true);
  };

  const toggleEditOfficeId = (officeId) => {
    setEditForm((prev) => {
      const id = Number(officeId);
      const has = prev.OfficeIds.includes(id);
      const OfficeIds = has ? prev.OfficeIds.filter((x) => x !== id) : [...prev.OfficeIds, id];
      return { ...prev, OfficeIds };
    });
  };

  const handleSaveEdit = async () => {
    if (editDocumentId == null) return;
    const title = (editForm.Title || "").trim();
    if (!title) {
      Common.showErrorMessage("Title is required.");
      return;
    }
    if (!editForm.AllOffices && (!editForm.OfficeIds || editForm.OfficeIds.length === 0)) {
      Common.showErrorMessage("Select at least one office, or enable All offices.");
      return;
    }

    setEditLoading(true);
    try {
      const categoryKey = editForm.Category;
      const categoryValue =
        COMPANY_DOCUMENT_CATEGORY_VALUE[categoryKey] !== undefined
          ? COMPANY_DOCUMENT_CATEGORY_VALUE[categoryKey]
          : COMPANY_DOCUMENT_CATEGORY_VALUE.Other;

      const documentIdStr = String(editDocumentId);
      const apiPayload = {
        documentId: documentIdStr,
        title,
        category: categoryValue,
        priority: Number(editForm.Priority),
        isActive: !!editForm.IsActive,
        requiresAcceptance: !!editForm.RequiresAcceptance,
        allowRejection: !!editForm.AllowRejection,
        officeScope: editForm.AllOffices
          ? CompanyDocumentOfficeScope.AllOffices
          : CompanyDocumentOfficeScope.SpecificOffices,
        officeIds: editForm.AllOffices ? [] : editForm.OfficeIds.map(Number),
      };

      const { CompanyDocuments } = config.REST_API;
      const updatePath = `${CompanyDocuments.UpdateCompanyDocument}/${encodeURIComponent(documentIdStr)}`;
      const editResponse = await axiosBasic.put(updatePath, apiPayload, {
        headers: { "Content-Type": "application/json" },
      });

      const result = editResponse?.data;
      if (!result || result.success !== true) {
        Common.showErrorMessage(result?.message || "Document could not be updated.");
        return;
      }

      setIsEditOpen(false);
      resetEditDialog();
      Common.showSuccessMessage(result.message || "Document updated successfully.");
      await fetchCompanyDocuments();
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Update failed");
    } finally {
      setEditLoading(false);
    }
  };

  const handleFilePicked = (file) => {
    if (!file) return;
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || null;
    handleFilePicked(file);
  };

  const preventDrag = (e) => {
    e.preventDefault();
  };

  const extractUploadedUrlFromPresignedResponse = (presignedUrlResponse) => {
    // Try common response shapes used elsewhere in the app.
    // Keep every field (contentType, fileUrl, etc.) — they must match the signed S3 request.
    const data = presignedUrlResponse?.data;
    if (data?.data?.presignedUrl) return data.data;
    if (data?.presignedUrl) return { ...data };
    if (data?.data) return data.data;
    return null;
  };

  const handleUploadDocument = async () => {
    try {
      if (!selectedFile) {
        Common.showErrorMessage("Please select a document file first.");
        return;
      }
      const title = (uploadForm.Title || "").trim();
      if (!title) {
        Common.showErrorMessage("Title is required.");
        return;
      }
      if (!uploadForm.AllOffices && (!uploadForm.OfficeIds || uploadForm.OfficeIds.length === 0)) {
        Common.showErrorMessage("Select at least one office, or enable All offices.");
        return;
      }

      setUploadLoading(true);

      const { S3Upload, CompanyDocuments } = config.REST_API;

      // 1) Request a pre-signed URL from backend for S3 upload
      const uniqueTimestamp = Date.now();
      const originalName = selectedFile.name || "document";
      const fileExt = originalName.includes(".") ? originalName.split(".").pop() : "";
      const safeExt = fileExt ? `.${fileExt}` : "";
      const fileNameForS3 = `company-document-${uniqueTimestamp}${safeExt}`;

      const presignedUrlResponse = await axiosBasic.post(
        S3Upload.GetDocumentUploadPreSignedUrl,
        { fileName: fileNameForS3, type: "Doc" }
      );

      const presignedObj = extractUploadedUrlFromPresignedResponse(presignedUrlResponse);
      if (!presignedObj?.presignedUrl) {
        throw new Error("Failed to get a valid presigned URL for document upload.");
      }

      // 2) Upload file bytes directly to S3 (document-specific Content-Type / URL handling)
      let fileUrl;
      try {
        const uploadedUrl = await uploadDocumentToS3("PUT", presignedObj, selectedFile);
        fileUrl = uploadedUrl != null ? String(uploadedUrl).trim() : "";
      } catch (uploadErr) {
        Common.showErrorMessage(
          uploadErr?.response?.data?.message || uploadErr?.message || "File upload to storage failed."
        );
        return;
      }
      if (!fileUrl) {
        Common.showErrorMessage("File upload did not return a valid URL.");
        return;
      }

      // 3) Create document record in app backend
      // ASP.NET Core default JSON uses camelCase; PascalCase often fails binding and the model arrives null.
      const categoryKey = uploadForm.Category;
      const categoryValue =
        COMPANY_DOCUMENT_CATEGORY_VALUE[categoryKey] !== undefined
          ? COMPANY_DOCUMENT_CATEGORY_VALUE[categoryKey]
          : COMPANY_DOCUMENT_CATEGORY_VALUE.Other;
      const description = (uploadForm.Description || "").trim();

      const apiPayload = {
        title,
        description: description.length ? description : null,
        category: categoryValue,
        fileUrl,
        fileName: originalName,
        fileType: selectedFile.type || (fileExt ? `.${fileExt}` : "application/octet-stream"),
        fileSize: Number(selectedFile.size),
        requiresAcceptance: !!uploadForm.RequiresAcceptance,
        allowRejection: !!uploadForm.AllowRejection,
        isActive: !!uploadForm.IsActive,
        priority: Number(uploadForm.Priority),
        officeScope: uploadForm.AllOffices
          ? CompanyDocumentOfficeScope.AllOffices
          : CompanyDocumentOfficeScope.SpecificOffices,
        officeIds: uploadForm.AllOffices ? [] : uploadForm.OfficeIds.map(Number),
      };

      const uploadResponse = await axiosBasic.post(CompanyDocuments.UploadDocument, apiPayload, {
        headers: { "Content-Type": "application/json" },
      });

      const result = uploadResponse?.data;
      if (!result || result.success !== true) {
        Common.showErrorMessage(result?.message || "Document could not be saved.");
        return;
      }

      setIsUploadOpen(false);
      resetUploadDialog();
      Common.showSuccessMessage(result.message || "Document uploaded successfully!");
      setPage(1);
      await fetchCompanyDocuments({ page: 1 });
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="documents-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-folder2-open" />}
        title={t("app.documents.libraryTitle")}
        description={t("app.documents.libraryDescription")}
        rightContent={
          <>
            <div className="common-page-header-stat-card" title="Total documents in your library">
              <span className="common-page-header-stat-label">Total assets</span>
              <span className="common-page-header-stat-value">{totalCount}</span>
            </div>
            <button
              type="button"
              className="documents-upload-primary-btn"
              onClick={() => withWorkplace(() => setIsUploadOpen(true))}
            >
              <i className="bi bi-cloud-arrow-up" aria-hidden />
              <span>Upload document</span>
            </button>
          </>
        }
      />

      <div className="documents-filter-bar">
        <div className="documents-filter-bar-left">
          <div className="documents-filter-search-wrap">
            <i className="bi bi-search documents-filter-search-icon" aria-hidden />
            <input
              type="search"
              className="documents-input documents-filter-search-input"
              placeholder="Search title, description, or file name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search documents"
              autoComplete="off"
            />
            {searchInput ? (
              <button
                type="button"
                className="documents-filter-search-clear"
                onClick={() => setSearchInput("")}
                aria-label="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="documents-filter-office">
            <i className="bi bi-building documents-filter-office-icon" aria-hidden />
            <select
              className="documents-input documents-filter-select"
              value={filterOfficeId != null ? String(filterOfficeId) : ""}
              onChange={(e) => {
                const v = e.target.value;
                setFilterOfficeId(v === "" ? null : v);
                setPage(1);
              }}
              aria-label="Filter by office"
            >
              <option value="">All offices</option>
              {officesForFilter.map((o) => (
                <option key={String(o.id)} value={String(o.id)}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="documents-filter-bar-right">
          <RowsPerPageSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            options={[10, 20, 50, 100]}
            label="Rows per page"
            ariaLabel="Number of records per page"
            wrapperClassName="documents-filter-page-size"
            labelClassName="documents-filter-page-size-label"
            selectClassName="documents-input documents-filter-select documents-filter-page-size-ddl"
          />
          <label className="documents-filter-inline">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => {
                setIncludeInactive(e.target.checked);
                setPage(1);
              }}
            />
            <span>Include drafts (unpublished)</span>
          </label>
        </div>
      </div>

      <TableToolbar className="documents-table-toolbar">
        <FilterTabs
          tabs={categoryTabs}
          activeKey={categoryFilter}
          onChange={setCategoryFilter}
          ariaLabel="Filter by category"
          className="documents-category-tabs"
          tabClassName="documents-category-tab"
        />
      </TableToolbar>

      <TableContainer className="documents-table-wrap">
        <DataTable
          className="documents-table"
          columns={tableColumns}
          rows={filteredSortedDocuments}
          getRowKey={(item) => item.id}
          renderRow={(item) => (
            <>
              <td>{item.name}</td>
              <td>{getCategoryLabel(item.type)}</td>
              <td>{priorityLabels[item.priority] ?? "—"}</td>
              <td className="documents-table-td-offices" title={formatDistributionTooltip(item)}>
                {formatDistributionGridLabel(item)}
              </td>
              <td>
                <button
                  type="button"
                  className={`documents-switch ${item.status ? "is-on" : ""}`}
                  disabled={gridUpdatingId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateDocumentFromGrid(item, "status");
                  }}
                >
                  <span className="documents-switch-thumb" />
                </button>
                <span className={`documents-switch-text ${item.status ? "on" : "off"}`}>
                  {item.status ? "Published" : "Draft"}
                </span>
              </td>
              <td>
                <button
                  type="button"
                  className={`documents-switch ${item.ackRequired ? "is-on" : ""}`}
                  disabled={gridUpdatingId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateDocumentFromGrid(item, "ackRequired");
                  }}
                >
                  <span className="documents-switch-thumb" />
                </button>
                <span className={`documents-switch-text ${item.ackRequired ? "on" : "off"}`}>
                  {item.ackRequired ? "Required" : "Not Required"}
                </span>
              </td>
              <td>
                <button
                  type="button"
                  className={`documents-switch ${item.allowRejection ? "is-on" : ""}`}
                  disabled={gridUpdatingId === item.id || !item.ackRequired}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateDocumentFromGrid(item, "allowRejection");
                  }}
                >
                  <span className="documents-switch-thumb" />
                </button>
                <span className={`documents-switch-text ${item.allowRejection ? "on" : "off"}`}>
                  {item.allowRejection ? "Allowed" : "Not Allowed"}
                </span>
              </td>
              <td className="documents-table-td-actions">
                <div className="documents-actions-inline">
                  <button
                    type="button"
                    className="documents-grid-action-btn documents-grid-action-btn--view"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAdminView(item.id, item);
                    }}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="documents-grid-action-btn documents-grid-action-btn--edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDocument(item);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="documents-grid-action-btn documents-grid-action-btn--delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteConfirm(item);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </>
          )}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSortColumn}
          loading={listLoading}
          loadingMessage="Loading documents..."
          emptyMessage={tableEmptyMessage}
          stateCellStyle={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
        />
        <div
          className={`documents-pagination documents-pagination--grid-pager${listLoading ? " is-loading" : ""}`}
          aria-busy={listLoading}
        >
          <span className="documents-pagination-total">
            {totalCount === 0
              ? "No results"
              : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
          </span>
          <div className="documents-pagination-pager-wrap">
            <GridPager
              className="documents-rc-pagination"
              onChange={handleDocumentsPagerChange}
              current={page}
              total={totalCount}
              pageSize={pageSize}
              defaultPageSize={20}
              showLessItems
              showTitle={false}
              showSizeChanger={false}
            />
          </div>
        </div>
      </TableContainer>

      {adminView &&
        typeof document !== "undefined" &&
        createPortal(
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="doc-detail-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAdminView();
          }}
        >
          <div
            className="documents-modal documents-modal--admin-view"
            onClick={(e) => e.stopPropagation()}
          >
            {adminView.loading ? (
              <div className="documents-admin-view-loading-wrap">
                <h2 id="doc-detail-title" className="visually-hidden">
                  Loading document
                </h2>
                <button
                  type="button"
                  className="btn btn-primary documents-btn documents-admin-view-cancel-btn documents-admin-view-close-btn"
                  onClick={closeAdminView}
                >
                  Cancel
                </button>
                <Dimmer.Dimmable
                  as={Segment}
                  basic
                  dimmed
                  className="documents-admin-view-dimmable"
                  style={{
                    border: "none",
                    boxShadow: "none",
                    margin: 0,
                    padding: 0,
                    background: "#fff",
                  }}
                >
                  <Dimmer active inverted>
                    <Loader size="large">Loading document…</Loader>
                  </Dimmer>
                </Dimmer.Dimmable>
              </div>
            ) : adminView.error ? (
              <div className="documents-admin-view-loading">
                <p className="documents-detail-error" role="alert">
                  {adminView.error}
                </p>
                <button
                  type="button"
                  className="btn btn-primary documents-btn documents-admin-view-close-btn"
                  onClick={closeAdminView}
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="documents-admin-view-body">
                <div className="documents-admin-view-meta">
                  <div className="documents-upload-modal-header">
                    <div>
                      <p className="documents-upload-modal-eyebrow">Document details</p>
                      <h2 className="documents-upload-modal-title" id="doc-detail-title">
                        {adminView.row.name}
                      </h2>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary documents-btn documents-admin-view-close-btn"
                      aria-label="Close document details"
                      onClick={closeAdminView}
                    >
                      Close
                    </button>
                  </div>

                  {adminView.row.description ? (
                    <p className="documents-detail-description">{adminView.row.description}</p>
                  ) : null}

                  <div className="documents-detail-panel">
                    <dl className="documents-detail-dl">
                      <div className="documents-detail-dl-row">
                        <dt>Category</dt>
                        <dd>
                          <span className="documents-detail-badge documents-detail-badge--category">
                            {getCategoryLabel(adminView.row.type)}
                          </span>
                        </dd>
                      </div>
                      <div className="documents-detail-dl-row">
                        <dt>Priority</dt>
                        <dd>
                          <span className={priorityBadgeClass(adminView.row.priority)}>
                            {priorityLabels[adminView.row.priority] ?? "—"}
                          </span>
                        </dd>
                      </div>
                      <div className="documents-detail-dl-row">
                        <dt>Publication</dt>
                        <dd>
                          <span className={publicationBadgeClass(adminView.row.status)}>
                            {adminView.row.status ? "Published" : "Draft"}
                          </span>
                        </dd>
                      </div>
                      <div className="documents-detail-dl-row">
                        <dt>Distribution</dt>
                        <dd className="documents-detail-dl-dd--offices">
                          {adminView.row.officeScope === CompanyDocumentOfficeScope.AllOffices ? (
                            <span className="documents-detail-badge documents-detail-badge--distribution">
                              All offices
                            </span>
                          ) : adminView.row.applicableOffices?.length ? (
                            <ul className="documents-detail-office-names">
                              {adminView.row.applicableOffices.map((o) => (
                                <li key={String(o.id)}>{o.name}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="documents-detail-badge documents-detail-badge--distribution">
                              {adminView.row.officeIds?.length ?? 0} office(s) selected
                            </span>
                          )}
                        </dd>
                      </div>
                      <div className="documents-detail-dl-row">
                        <dt>Acknowledgement</dt>
                        <dd>
                          <span
                            className={
                              adminView.row.ackRequired
                                ? "documents-detail-badge documents-detail-badge--ack-required"
                                : "documents-detail-badge documents-detail-badge--ack-optional"
                            }
                          >
                            {adminView.row.ackRequired ? "Required" : "Not required"}
                          </span>
                        </dd>
                      </div>
                      <div className="documents-detail-dl-row">
                        <dt>Allow rejection</dt>
                        <dd>
                          <span
                            className={
                              adminView.row.allowRejection
                                ? "documents-detail-badge documents-detail-badge--bool-yes"
                                : "documents-detail-badge documents-detail-badge--bool-no"
                            }
                          >
                            {adminView.row.allowRejection ? "Yes" : "No"}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {!adminView.row.ackRequired ? (
                    <p className="documents-detail-no-ack">
                      Employee acknowledgement is not required for this document.
                    </p>
                  ) : !adminView.row.status ? (
                    <p className="documents-detail-draft-ack-hint">
                      Publish this document to start tracking employee acknowledgements. Response and acceptance
                      rates are computed from assigned employees once the document is live.
                    </p>
                  ) : adminView.acknowledgementSummary ? (
                    <DocumentAdminAcknowledgementSummary
                      summary={adminView.acknowledgementSummary}
                      allowRejection={adminView.row.allowRejection}
                    />
                  ) : (
                    <div className="documents-detail-completion">
                      <div className="documents-detail-completion-head">
                        <span className="documents-detail-completion-label">Acknowledgements</span>
                      </div>
                      <p className="documents-upload-section-hint" style={{ marginBottom: 0 }}>
                        Summary data was not returned for this document. Try again later or contact support if
                        this persists.
                      </p>
                    </div>
                  )}
                </div>

                <div className="documents-admin-view-preview">
                  <p className="documents-admin-preview-label">Preview</p>
                  {adminView.previewUrl && isBrowserPreviewableMime(adminView.contentType) ? (
                    <iframe
                      title="Document preview"
                      src={adminView.previewUrl}
                      className="documents-admin-preview-iframe"
                    />
                  ) : adminView.previewUrl ? (
                    <div className="documents-admin-preview-fallback">
                      <p className="documents-upload-section-hint">
                        Inline preview is not available for this file type ({adminView.contentType || "unknown"}).
                        Download the file to open it in the appropriate application.
                      </p>
                      <a
                        className="btn btn-primary documents-btn"
                        href={adminView.previewUrl}
                        download={adminView.fileName}
                      >
                        Download file
                      </a>
                    </div>
                  ) : (
                    <div className="documents-admin-preview-fallback">
                      <p className="documents-upload-section-hint">No file content was returned for this document.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {deleteConfirm && (
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-doc-confirm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) {
              setDeleteConfirm(null);
            }
          }}
        >
          <div
            className="documents-modal documents-modal--upload-single documents-modal--delete-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="documents-modal-preview"
              style={{ borderRight: "none", background: "#fff" }}
            >
              <div className="documents-upload-modal-header">
                <div>
                  <p className="documents-upload-modal-eyebrow">Company documents</p>
                  <h2 className="documents-upload-modal-title" id="delete-doc-confirm-title">
                    Delete document
                  </h2>
                </div>
                <button
                  type="button"
                  className="btn btn-light documents-btn"
                  aria-label="Close"
                  disabled={deleteLoading}
                  onClick={() => setDeleteConfirm(null)}
                >
                  Close
                </button>
              </div>

              <div className="documents-modal-sheet documents-modal-sheet--upload documents-delete-confirm-body">
                <div className="documents-delete-confirm-summary">
                  <div className="documents-delete-confirm-icon-wrap" aria-hidden>
                    <i className="bi bi-trash3" />
                  </div>
                  <p className="documents-delete-confirm-lead">
                    This permanently removes the document from the library. Employees will no longer see it.
                  </p>
                </div>

                <div className="documents-delete-confirm-target">
                  <p className="documents-form-label documents-delete-confirm-target-label">Document</p>
                  <p className="documents-delete-confirm-target-name">{deleteConfirm.name}</p>
                </div>

                <div className="documents-modal-actions documents-delete-confirm-actions">
                  <button
                    type="button"
                    className="btn btn-light documents-btn"
                    disabled={deleteLoading}
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn documents-btn documents-delete-confirm-btn"
                    disabled={deleteLoading}
                    onClick={handleConfirmDeleteDocument}
                  >
                    {deleteLoading ? "Deleting…" : "Delete document"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isUploadOpen && (
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !uploadLoading) {
              setIsUploadOpen(false);
              resetUploadDialog();
            }
          }}
        >
          <div
            className="documents-modal documents-modal--upload-single"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="documents-modal-preview" style={{ borderRight: "none", background: "#fff" }}>
              <div className="documents-upload-modal-header">
                <div>
                  <p className="documents-upload-modal-eyebrow">Company documents</p>
                  <h2 className="documents-upload-modal-title" id="upload-dialog-title">
                    Upload new document
                  </h2>
                </div>
                <button
                  className="btn btn-light documents-btn"
                  type="button"
                  aria-label="Close upload dialog"
                  onClick={() => {
                    if (!uploadLoading) {
                      setIsUploadOpen(false);
                      resetUploadDialog();
                    }
                  }}
                >
                  Close
                </button>
              </div>

              {!selectedFile ? (
                <div style={{ marginTop: "1rem" }}>
                  <div
                    className="documents-upload-dropzone"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (uploadLoading) return;
                      if (fileInputRef.current) fileInputRef.current.click();
                    }}
                    onKeyDown={(e) => {
                      if (uploadLoading) return;
                      if (e.key === "Enter" || e.key === " ") {
                        if (fileInputRef.current) fileInputRef.current.click();
                      }
                    }}
                    onDragOver={preventDrag}
                    onDrop={handleDrop}
                  >
                    <div className="documents-upload-dropzone-title">
                      Click to select or drag & drop
                    </div>
                    <div className="documents-upload-dropzone-subtitle">
                      Accepts PDF, DOCX, XLSX. Max size 25 MB.
                    </div>
                    <div className="documents-upload-dropzone-actions">
                      <button
                        type="button"
                        className="btn btn-primary documents-btn"
                        disabled={uploadLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (fileInputRef.current) fileInputRef.current.click();
                        }}
                      >
                        Choose File
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      className="documents-upload-input"
                      type="file"
                      accept=".pdf,.docx,.doc,.xlsx,.xls"
                      disabled={uploadLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleFilePicked(file);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="documents-modal-sheet documents-modal-sheet--upload">
                  <div className="documents-upload-file-card">
                    <div className="documents-upload-file-meta">
                      <div className="documents-upload-file-icon" aria-hidden>
                        <i className="bi bi-file-earmark-text" style={{ fontSize: "1.25rem", color: "#4f46e5" }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="documents-upload-file-name">{selectedFile.name}</div>
                        <div className="documents-upload-file-sub">
                          {selectedFile.type || "Unknown type"} · {(selectedFile.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-light documents-btn"
                      disabled={uploadLoading}
                      onClick={() => {
                        resetUploadDialog();
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  <section className="documents-upload-section" aria-labelledby="upload-section-details">
                    <h3 className="documents-upload-section-title" id="upload-section-details">
                      Document details
                    </h3>
                    <div className="documents-upload-grid-2">
                      <div>
                        <label className="documents-form-label" htmlFor="upload-title">
                          Title<span className="documents-form-required">*</span>
                        </label>
                        <input
                          id="upload-title"
                          className="documents-input"
                          value={uploadForm.Title}
                          disabled={uploadLoading}
                          onChange={(e) => setUploadForm((p) => ({ ...p, Title: e.target.value }))}
                          placeholder="e.g. Annual leave policy 2025"
                        />
                      </div>
                      <div>
                        <label className="documents-form-label" htmlFor="upload-category">
                          Category<span className="documents-form-required">*</span>
                        </label>
                        <select
                          id="upload-category"
                          className="documents-input"
                          value={uploadForm.Category}
                          disabled={uploadLoading}
                          onChange={(e) => setUploadForm((p) => ({ ...p, Category: e.target.value }))}
                        >
                          <option value="InternalPolicy">Internal Policy</option>
                          <option value="Regulatory">Regulatory</option>
                          <option value="Training">Training</option>
                          <option value="TermsAndConditions">Terms &amp; Conditions</option>
                          <option value="EmployeeHandbook">Employee Handbook</option>
                          <option value="Safety">Safety</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="documents-form-label" htmlFor="upload-priority">
                          Priority
                        </label>
                        <select
                          id="upload-priority"
                          className="documents-input"
                          value={uploadForm.Priority}
                          disabled={uploadLoading}
                          onChange={(e) =>
                            setUploadForm((p) => ({ ...p, Priority: Number(e.target.value) }))
                          }
                        >
                          <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.Low}>Low</option>
                          <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.Medium}>Medium</option>
                          <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.High}>High</option>
                          <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.Critical}>Critical</option>
                          <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.FollowUp}>Follow-up</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  <section className="documents-upload-section" aria-labelledby="upload-section-description">
                    <h3 className="documents-upload-section-title" id="upload-section-description">
                      Description
                    </h3>
                    <label className="documents-form-label visually-hidden" htmlFor="upload-desc">
                      Optional description for employees
                    </label>
                    <textarea
                      id="upload-desc"
                      className="documents-textarea"
                      value={uploadForm.Description}
                      disabled={uploadLoading}
                      onChange={(e) => setUploadForm((p) => ({ ...p, Description: e.target.value }))}
                      placeholder="Optional context or summary for the team…"
                      rows={4}
                    />
                  </section>

                  <section className="documents-upload-section" aria-labelledby="upload-section-offices">
                    <h3 className="documents-upload-section-title" id="upload-section-offices">
                      Distribution
                    </h3>
                    <p className="documents-upload-section-hint" style={{ marginTop: 0, marginBottom: "0.65rem" }}>
                      Choose whether this document applies company-wide or only to selected workplaces.
                    </p>
                    <div className="documents-switch-field">
                      <div className="documents-switch-row">
                        <button
                          type="button"
                          className={`documents-switch ${uploadForm.AllOffices ? "is-on" : ""}`}
                          disabled={uploadLoading}
                          onClick={() =>
                            setUploadForm((p) => {
                              const nextAll = !p.AllOffices;
                              return { ...p, AllOffices: nextAll, OfficeIds: nextAll ? [] : p.OfficeIds };
                            })
                          }
                        >
                          <span className="documents-switch-thumb" />
                        </button>
                        <span className={`documents-switch-text ${uploadForm.AllOffices ? "on" : "off"}`}>
                          {uploadForm.AllOffices ? "All offices" : "Specific offices only"}
                        </span>
                      </div>
                      {!uploadForm.AllOffices && (
                        <div className="documents-office-checkbox-list" style={{ marginTop: "0.35rem" }}>
                          {companyOffices.length === 0 ? (
                            <div className="documents-card-subtitle" style={{ margin: 0 }}>
                              Loading offices…
                            </div>
                          ) : (
                            companyOffices.map((o) => (
                              <label key={o.id} className="documents-office-checkbox-row">
                                <input
                                  type="checkbox"
                                  checked={uploadForm.OfficeIds.includes(o.id)}
                                  disabled={uploadLoading}
                                  onChange={() => toggleUploadOfficeId(o.id)}
                                />
                                <span>{o.label}</span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="documents-upload-section" aria-labelledby="upload-section-ack">
                    <h3 className="documents-upload-section-title" id="upload-section-ack">
                      Acknowledgement
                    </h3>
                    <div className="documents-upload-toggle-grid">
                      <div className="documents-switch-field">
                        <span className="documents-form-label">Require employee acceptance</span>
                        <div className="documents-switch-row">
                          <button
                            type="button"
                            className={`documents-switch ${uploadForm.RequiresAcceptance ? "is-on" : ""}`}
                            disabled={uploadLoading}
                            onClick={() => {
                              setUploadForm((p) => ({
                                ...p,
                                RequiresAcceptance: !p.RequiresAcceptance,
                                AllowRejection: p.RequiresAcceptance ? true : p.AllowRejection,
                              }));
                            }}
                          >
                            <span className="documents-switch-thumb" />
                          </button>
                          <span className={`documents-switch-text ${uploadForm.RequiresAcceptance ? "on" : "off"}`}>
                            {uploadForm.RequiresAcceptance ? "Required" : "Not required"}
                          </span>
                        </div>
                      </div>
                      <div className="documents-switch-field">
                        <span className="documents-form-label">Allow rejection with comment</span>
                        <div className="documents-switch-row">
                          <button
                            type="button"
                            className={`documents-switch ${uploadForm.AllowRejection ? "is-on" : ""}`}
                            disabled={!uploadForm.RequiresAcceptance || uploadLoading}
                            onClick={() => {
                              if (!uploadForm.RequiresAcceptance) return;
                              setUploadForm((p) => ({ ...p, AllowRejection: !p.AllowRejection }));
                            }}
                          >
                            <span className="documents-switch-thumb" />
                          </button>
                          <span className={`documents-switch-text ${uploadForm.AllowRejection ? "on" : "off"}`}>
                            {uploadForm.AllowRejection ? "Allowed" : "Not allowed"}
                          </span>
                        </div>
                        {!uploadForm.RequiresAcceptance && (
                          <p className="documents-upload-section-hint">Turn on acceptance to use this option.</p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="documents-upload-section" aria-labelledby="upload-section-publish">
                    <h3 className="documents-upload-section-title" id="upload-section-publish">
                      Publication
                    </h3>
                    <div className="documents-switch-field">
                      <div className="documents-switch-row">
                        <button
                          type="button"
                          className={`documents-switch ${uploadForm.IsActive ? "is-on" : ""}`}
                          disabled={uploadLoading}
                          onClick={() => setUploadForm((p) => ({ ...p, IsActive: !p.IsActive }))}
                        >
                          <span className="documents-switch-thumb" />
                        </button>
                        <span className={`documents-switch-text ${uploadForm.IsActive ? "on" : "off"}`}>
                          {uploadForm.IsActive ? "Published" : "Draft"}
                        </span>
                      </div>
                      <p className="documents-upload-section-hint">
                        Drafts are only visible to admins until you publish.
                      </p>
                    </div>
                  </section>

                  <div className="documents-modal-actions">
                      <button
                        type="button"
                        className="btn btn-light documents-btn"
                        disabled={uploadLoading}
                        onClick={() => {
                          setIsUploadOpen(false);
                          resetUploadDialog();
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary documents-btn"
                        disabled={uploadLoading}
                        onClick={handleUploadDocument}
                      >
                        {uploadLoading ? "Uploading..." : "Upload Document"}
                      </button>
                    </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isEditOpen && (
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !editLoading) {
              setIsEditOpen(false);
              resetEditDialog();
            }
          }}
        >
          <div
            className="documents-modal documents-modal--upload-single"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="documents-modal-preview" style={{ borderRight: "none", background: "#fff" }}>
              <div className="documents-upload-modal-header">
                <div>
                  <p className="documents-upload-modal-eyebrow">Company documents</p>
                  <h2 className="documents-upload-modal-title" id="edit-dialog-title">
                    Edit document
                  </h2>
                </div>
                <button
                  className="btn btn-light documents-btn"
                  type="button"
                  aria-label="Close edit dialog"
                  onClick={() => {
                    if (!editLoading) {
                      setIsEditOpen(false);
                      resetEditDialog();
                    }
                  }}
                >
                  Close
                </button>
              </div>

              <div className="documents-modal-sheet documents-modal-sheet--upload" style={{ marginTop: "1rem" }}>
                <section className="documents-upload-section" aria-labelledby="edit-section-details">
                  <h3 className="documents-upload-section-title" id="edit-section-details">
                    Document details
                  </h3>
                  <div className="documents-upload-grid-2">
                    <div>
                      <label className="documents-form-label" htmlFor="edit-title">
                        Title<span className="documents-form-required">*</span>
                      </label>
                      <input
                        id="edit-title"
                        className="documents-input"
                        value={editForm.Title}
                        disabled={editLoading}
                        onChange={(e) => setEditForm((p) => ({ ...p, Title: e.target.value }))}
                        placeholder="e.g. Annual leave policy 2025"
                      />
                    </div>
                    <div>
                      <label className="documents-form-label" htmlFor="edit-category">
                        Category<span className="documents-form-required">*</span>
                      </label>
                      <select
                        id="edit-category"
                        className="documents-input"
                        value={editForm.Category}
                        disabled={editLoading}
                        onChange={(e) => setEditForm((p) => ({ ...p, Category: e.target.value }))}
                      >
                        <option value="InternalPolicy">Internal Policy</option>
                        <option value="Regulatory">Regulatory</option>
                        <option value="Training">Training</option>
                        <option value="TermsAndConditions">Terms &amp; Conditions</option>
                        <option value="EmployeeHandbook">Employee Handbook</option>
                        <option value="Safety">Safety</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="documents-form-label" htmlFor="edit-priority">
                        Priority
                      </label>
                      <select
                        id="edit-priority"
                        className="documents-input"
                        value={editForm.Priority}
                        disabled={editLoading}
                        onChange={(e) =>
                          setEditForm((p) => ({ ...p, Priority: Number(e.target.value) }))
                        }
                      >
                        <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.Low}>Low</option>
                        <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.Medium}>Medium</option>
                        <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.High}>High</option>
                        <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.Critical}>Critical</option>
                        <option value={COMPANY_DOCUMENT_PRIORITY_VALUE.FollowUp}>Follow-up</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section className="documents-upload-section" aria-labelledby="edit-section-offices">
                  <h3 className="documents-upload-section-title" id="edit-section-offices">
                    Distribution
                  </h3>
                  <p className="documents-upload-section-hint" style={{ marginTop: 0, marginBottom: "0.65rem" }}>
                    Choose whether this document applies company-wide or only to selected workplaces.
                  </p>
                  <div className="documents-switch-field">
                    <div className="documents-switch-row">
                      <button
                        type="button"
                        className={`documents-switch ${editForm.AllOffices ? "is-on" : ""}`}
                        disabled={editLoading}
                        onClick={() =>
                          setEditForm((p) => {
                            const nextAll = !p.AllOffices;
                            return { ...p, AllOffices: nextAll, OfficeIds: nextAll ? [] : p.OfficeIds };
                          })
                        }
                      >
                        <span className="documents-switch-thumb" />
                      </button>
                      <span className={`documents-switch-text ${editForm.AllOffices ? "on" : "off"}`}>
                        {editForm.AllOffices ? "All offices" : "Specific offices only"}
                      </span>
                    </div>
                    {!editForm.AllOffices && (
                      <div className="documents-office-checkbox-list" style={{ marginTop: "0.35rem" }}>
                        {companyOffices.length === 0 ? (
                          <div className="documents-card-subtitle" style={{ margin: 0 }}>
                            Loading offices…
                          </div>
                        ) : (
                          companyOffices.map((o) => (
                            <label key={o.id} className="documents-office-checkbox-row">
                              <input
                                type="checkbox"
                                checked={editForm.OfficeIds.includes(o.id)}
                                disabled={editLoading}
                                onChange={() => toggleEditOfficeId(o.id)}
                              />
                              <span>{o.label}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </section>

                <section className="documents-upload-section" aria-labelledby="edit-section-ack">
                  <h3 className="documents-upload-section-title" id="edit-section-ack">
                    Acknowledgement
                  </h3>
                  <div className="documents-upload-toggle-grid">
                    <div className="documents-switch-field">
                      <span className="documents-form-label">Require employee acceptance</span>
                      <div className="documents-switch-row">
                        <button
                          type="button"
                          className={`documents-switch ${editForm.RequiresAcceptance ? "is-on" : ""}`}
                          disabled={editLoading}
                          onClick={() => {
                            setEditForm((p) => ({
                              ...p,
                              RequiresAcceptance: !p.RequiresAcceptance,
                              AllowRejection: p.RequiresAcceptance ? true : p.AllowRejection,
                            }));
                          }}
                        >
                          <span className="documents-switch-thumb" />
                        </button>
                        <span className={`documents-switch-text ${editForm.RequiresAcceptance ? "on" : "off"}`}>
                          {editForm.RequiresAcceptance ? "Required" : "Not required"}
                        </span>
                      </div>
                    </div>
                    <div className="documents-switch-field">
                      <span className="documents-form-label">Allow rejection with comment</span>
                      <div className="documents-switch-row">
                        <button
                          type="button"
                          className={`documents-switch ${editForm.AllowRejection ? "is-on" : ""}`}
                          disabled={!editForm.RequiresAcceptance || editLoading}
                          onClick={() => {
                            if (!editForm.RequiresAcceptance) return;
                            setEditForm((p) => ({ ...p, AllowRejection: !p.AllowRejection }));
                          }}
                        >
                          <span className="documents-switch-thumb" />
                        </button>
                        <span className={`documents-switch-text ${editForm.AllowRejection ? "on" : "off"}`}>
                          {editForm.AllowRejection ? "Allowed" : "Not allowed"}
                        </span>
                      </div>
                      {!editForm.RequiresAcceptance && (
                        <p className="documents-upload-section-hint">Turn on acceptance to use this option.</p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="documents-upload-section" aria-labelledby="edit-section-publish">
                  <h3 className="documents-upload-section-title" id="edit-section-publish">
                    Publication
                  </h3>
                  <div className="documents-switch-field">
                    <div className="documents-switch-row">
                      <button
                        type="button"
                        className={`documents-switch ${editForm.IsActive ? "is-on" : ""}`}
                        disabled={editLoading}
                        onClick={() => setEditForm((p) => ({ ...p, IsActive: !p.IsActive }))}
                      >
                        <span className="documents-switch-thumb" />
                      </button>
                      <span className={`documents-switch-text ${editForm.IsActive ? "on" : "off"}`}>
                        {editForm.IsActive ? "Published" : "Draft"}
                      </span>
                    </div>
                    <p className="documents-upload-section-hint">
                      Drafts are only visible to admins until you publish.
                    </p>
                  </div>
                </section>

                <div className="documents-modal-actions">
                  <button
                    type="button"
                    className="btn btn-light documents-btn"
                    disabled={editLoading}
                    onClick={() => {
                      setIsEditOpen(false);
                      resetEditDialog();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary documents-btn"
                    disabled={editLoading}
                    onClick={handleSaveEdit}
                  >
                    {editLoading ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {workplaceRequiredModal}
    </div>
  );
};

export default DocumentLibrary;
